import { useState } from 'react'
import { Component, LocaleCode, BigCommerceProductData } from '../types/component'

interface ProductDetailProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
  cartId?: string | null
  wishlistId?: string | null
  onCartUpdate?: (cartId: string) => void
  onWishlistUpdate?: (wishlistId: string) => void
}

export function ProductDetail({
  component,
  locale,
  storeId,
  apiUrl,
  style,
  cartId,
  wishlistId,
  onCartUpdate,
  onWishlistUpdate,
}: ProductDetailProps) {
  const config = component.config as any
  const product = component.bigcommerceProduct as BigCommerceProductData | null

  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({})
  const [isAddingToCart, setIsAddingToCart] = useState(false)
  const [isAddingToWishlist, setIsAddingToWishlist] = useState(false)
  const [isInWishlist, setIsInWishlist] = useState(false)

  const layout = (config.layout as string) || 'standard'
  const showBreadcrumbs = (config.showBreadcrumbs as boolean) !== false
  const showBrand = (config.showBrand as boolean) !== false
  const showSku = (config.showSku as boolean) !== false
  const showAvailability = (config.showAvailability as boolean) !== false
  const showDescription = (config.showDescription as boolean) !== false
  const showVariants = (config.showVariants as boolean) !== false
  const addToCartButtonText = (config.addToCartButtonText as string) || 'Add to Cart'
  const showWishlistButton = (config.showWishlistButton as boolean) !== false

  if (!product) {
    return (
      <div style={style} className="p-6 text-center text-muted-foreground border rounded">
        Select a product to display
      </div>
    )
  }

  const images = product.images || (product.imageUrl ? [product.imageUrl] : [])
  const currentImage = images[selectedImage] || images[0] || ''

  const selectedVariant = product.variants?.find((variant) => {
    return variant.optionValues.every((optionValue) => {
      return selectedOptions[optionValue.optionId] === optionValue.id
    })
  })

  const displayPrice = selectedVariant?.price || product.calculatedPrice || product.price || 0
  const salePrice = product.salePrice

  const handleAddToCart = async () => {
    if (!product || !storeId) return

    try {
      setIsAddingToCart(true)
      const lineItem = {
        product_id: Number(product.productId),
        quantity: quantity,
        variant_id: selectedVariant?.id ? Number(selectedVariant.id) : undefined,
      }

      let currentCartId = cartId

      if (!currentCartId) {
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lineItems: [lineItem] }),
        })
        if (!response.ok) throw new Error('Failed to create cart')
        const data = await response.json()
        currentCartId = data.data.id
      } else {
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/carts/${storeId}/${currentCartId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lineItems: [lineItem] }),
        })
        if (!response.ok) throw new Error('Failed to add to cart')
      }

      if (onCartUpdate && currentCartId) {
        onCartUpdate(currentCartId)
      }
    } catch (error) {
      console.error('Error adding to cart:', error)
    } finally {
      setIsAddingToCart(false)
    }
  }

  const handleToggleWishlist = async () => {
    if (!product || !storeId) return

    try {
      setIsAddingToWishlist(true)

      let currentWishlistId = wishlistId
      if (!currentWishlistId) {
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/${storeId}/wishlists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: 'My Wishlist' }),
        })
        if (!response.ok) throw new Error('Failed to create wishlist')
        const data = await response.json()
        currentWishlistId = data.data.id
      }

      if (!isInWishlist) {
        const response = await fetch(`${apiUrl}/api/v1/bigcommerce/${storeId}/wishlists/${currentWishlistId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product_id: Number(product.productId),
            variant_id: selectedVariant?.id ? Number(selectedVariant.id) : undefined,
          }),
        })
        if (!response.ok) throw new Error('Failed to add to wishlist')

        if (onWishlistUpdate && currentWishlistId) {
          onWishlistUpdate(currentWishlistId)
        }

        setIsInWishlist(true)
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error)
    } finally {
      setIsAddingToWishlist(false)
    }
  }

  const imageGallery = (
    <div className="space-y-4 max-w-md">
      {/* Main Image */}
      <div className="aspect-square bg-muted rounded-lg overflow-hidden">
        {currentImage ? (
          <img
            src={currentImage}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            No image
          </div>
        )}
      </div>

      {/* Thumbnail Gallery */}
      {images.length > 1 && (
        <div className="grid grid-cols-5 gap-2">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedImage(idx)}
              className={`aspect-square rounded overflow-hidden border-2 transition-all ${
                selectedImage === idx ? 'border-primary' : 'border-transparent hover:border-muted-foreground'
              }`}
            >
              <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  )

  const productInfo = (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      {showBreadcrumbs && product.categories && product.categories.length > 0 && (
        <div className="text-sm text-muted-foreground">
          Home / {product.categories[0].name} / {product.name}
        </div>
      )}

      {/* Brand */}
      {showBrand && product.brand && (
        <div className="text-sm text-muted-foreground">{product.brand.name}</div>
      )}

      {/* Product Name */}
      <h1 className="text-3xl font-bold">{product.name}</h1>

      {/* Price */}
      <div className="flex items-baseline gap-3">
        {salePrice && salePrice < displayPrice ? (
          <>
            <span className="text-3xl font-bold text-destructive">
              ${salePrice.toFixed(2)}
            </span>
            <span className="text-xl text-muted-foreground line-through">
              ${displayPrice.toFixed(2)}
            </span>
          </>
        ) : (
          <span className="text-3xl font-bold">${displayPrice.toFixed(2)}</span>
        )}
      </div>

      {/* SKU and Availability */}
      <div className="flex gap-4 text-sm">
        {showSku && product.sku && (
          <div>
            <span className="text-muted-foreground">SKU:</span> {product.sku}
          </div>
        )}
        {showAvailability && (
          <div>
            <span className="text-muted-foreground">Status:</span>{' '}
            <span
              className={
                product.availability === 'available'
                  ? 'text-green-600'
                  : 'text-muted-foreground'
              }
            >
              {product.availability === 'available' ? 'In Stock' : 'Out of Stock'}
            </span>
          </div>
        )}
      </div>

      {/* Description */}
      {showDescription && product.description && (
        <div className="prose prose-sm max-w-none">
          <p>{product.description}</p>
        </div>
      )}

      {/* Variant Options */}
      {showVariants && product.options && product.options.length > 0 && (
        <div className="space-y-4">
          {product.options.map((option) => (
            <div key={option.id}>
              <label className="block text-sm font-medium mb-2">
                {option.displayName}
              </label>
              <div className="flex flex-wrap gap-2">
                {option.values.map((value) => (
                  <button
                    key={value.id}
                    onClick={() =>
                      setSelectedOptions((prev) => ({
                        ...prev,
                        [option.id]: value.id,
                      }))
                    }
                    className={`px-4 py-2 border rounded transition-all ${
                      selectedOptions[option.id] === value.id
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted hover:border-primary'
                    }`}
                  >
                    {value.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quantity */}
      <div>
        <label className="block text-sm font-medium mb-2">Quantity</label>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-10 h-10 border rounded hover:bg-accent"
          >
            −
          </button>
          <span className="w-12 text-center">{quantity}</span>
          <button
            onClick={() => setQuantity(quantity + 1)}
            className="w-10 h-10 border rounded hover:bg-accent"
          >
            +
          </button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={handleAddToCart}
          disabled={isAddingToCart || product.availability !== 'available'}
          className="flex-1 px-6 py-3 bg-primary text-primary-foreground rounded font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAddingToCart ? 'Adding...' : addToCartButtonText}
        </button>

        {showWishlistButton && (
          <button
            onClick={handleToggleWishlist}
            disabled={isAddingToWishlist}
            className="w-12 h-12 border rounded hover:bg-accent flex items-center justify-center disabled:opacity-50"
            aria-label={isInWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill={isInWishlist ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={isInWishlist ? 'text-red-500' : ''}
            >
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  )

  if (layout === 'gallery-left') {
    return (
      <div style={style} className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
        {imageGallery}
        {productInfo}
      </div>
    )
  }

  if (layout === 'gallery-right') {
    return (
      <div style={style} className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
        {productInfo}
        {imageGallery}
      </div>
    )
  }

  // Standard layout
  return (
    <div style={style} className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full">
      {imageGallery}
      {productInfo}
    </div>
  )
}
